import { motion } from 'motion/react';
import { CheckCircle2, Stethoscope, Clock, ShieldCheck, Heart, ArrowRight, Target, Eye, Award, Users, Activity, Zap, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import PlansSection from '../components/planos/PlansSection';

export default function Home() {
  const differentials = [
    { title: 'Selo de Qualidade', description: 'Avaliação máxima e acreditação ANS (98,3% de conformidade)', icon: Award },
    { title: 'Experiência', description: '15 anos de mercado e mais de 3.000 pacientes assistidos', icon: Clock },
    { title: 'Volume de Atendimento', description: '60.000+ consultas presenciais e 800.000+ sessões realizadas', icon: Users },
    { title: 'Estrutura Própria', description: 'Sede e Farmácia próprias e modernizadas para seu conforto', icon: Stethoscope },
    { title: 'UTI Móvel', description: 'Totalmente equipada, uma das mais modernas do mercado', icon: ShieldCheck },
    { title: 'Prontuário Digital', description: 'Integração total com prescrição digital (ICP-Brasil)', icon: Target },
    { title: 'Protocolos Seguros', description: 'Suporte em evidências científicas (Resolução CFM nº 2.314/2022)', icon: ShieldCheck },
    { title: 'Resolutividade', description: 'Uma das melhores avaliações do segmento em solução de casos', icon: Heart }
  ];

  const whyUs = [
    { title: 'Baixo Custo', description: 'Agendamento de diversas especialidades médicas com valores acessíveis.', icon: CheckCircle2 },
    { title: 'Medicamento em Casa', description: 'Descontos progressivos e entrega grátis para Brasília e entorno (*).', icon: CheckCircle2 },
    { title: 'Acreditação ANS', description: 'Única empresa do segmento com Selo de Qualidade e Acreditação ANS.', icon: CheckCircle2 },
    { title: 'Experiência Consolidada', description: '15 anos de atuação com protocolos clínicos bem definidos.', icon: CheckCircle2 },
    { title: 'Humanização Digital', description: 'Excelência do atendimento presencial trazida para a telemedicina.', icon: CheckCircle2 },
    { title: 'Preços Promocionais', description: 'Valores abaixo da média de mercado para assinantes.', icon: CheckCircle2 }
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?q=80&w=2070&auto=format&fit=crop"
            alt="Telemedicina Novità"
            className="w-full h-full object-cover animate-slow-zoom"
            referrerPolicy="no-referrer"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-900/90 via-stone-900/60 to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-primary/20 border border-primary/30 text-primary text-xs font-bold uppercase tracking-wider mb-6">
                <Activity className="w-4 h-4" />
                <span>Acreditação ANS - Selo de Qualidade</span>
              </div>
              <h1 className="text-5xl lg:text-7xl font-black text-white leading-[1.1] mb-8 font-display">
                Sua Saúde <br />
                <span className="text-primary">Onde você estiver.</span>
              </h1>
              <p className="text-xl text-text-muted mb-10 leading-relaxed font-medium">
                A excelência do atendimento presencial agora na palma da sua mão. Tecnologia de ponta com o cuidado humanizado que você merece.
              </p>
              <div className="flex flex-wrap gap-5">
                <Link to="/signup" className="btn-primary">
                  Começar Agora <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
                <a href="#planos" className="px-8 py-4 bg-white/10 backdrop-blur-md text-white font-bold rounded-2xl border border-white/20 hover:bg-white/20 transition-all">
                  Ver Planos
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative z-20 -mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Anos de Mercado", value: "15+", icon: Award },
              { label: "Pacientes Felizes", value: "3.000+", icon: Users },
              { label: "Consultas Realizadas", value: "800k+", icon: Activity },
              { label: "Atendimento 24/7", value: "100%", icon: Clock }
            ].map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="glass-card p-6 rounded-2xl text-center"
              >
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary mb-3">
                  <stat.icon className="w-5 h-5" />
                </div>
                <div className="text-2xl font-black text-text-main">{stat.value}</div>
                <div className="text-xs font-bold text-text-muted uppercase tracking-wide">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section-padding bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-24 items-center">
            <div className="relative">
              <div className="absolute -top-10 -left-10 w-40 h-40 bg-primary/10 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-primary/20 rounded-full blur-3xl"></div>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="relative z-10"
              >
                <img
                  src="https://images.unsplash.com/photo-1584982751601-97dcc096659c?q=80&w=2000&auto=format&fit=crop"
                  alt="Atendimento Novità"
                  className="rounded-[2.5rem] shadow-2xl w-full aspect-[4/5] object-cover"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-2xl shadow-xl border border-text-muted/10 max-w-[240px]">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse"></div>
                    <span className="text-xs font-bold text-text-muted uppercase">Online Agora</span>
                  </div>
                  <p className="text-sm font-bold text-text-main">Médicos disponíveis para atendimento imediato.</p>
                </div>
              </motion.div>
            </div>

            <div className="mt-20 lg:mt-0">
              <h2 className="text-4xl lg:text-5xl font-black text-text-main mb-8 leading-tight font-display">
                Quem <span className="text-primary">Somos</span>
              </h2>
              <div className="space-y-6 text-lg text-text-main/80 leading-relaxed">
                <p>
                  A Novità nasceu do desejo de transformar o acesso à saúde no Brasil. Com anos de experiência no setor médico, percebemos que a tecnologia poderia ser a ponte para um atendimento mais ágil, sem perder o calor humano.
                </p>
                <p>
                  Nossa plataforma foi desenvolvida para oferecer não apenas consultas, mas uma jornada completa de cuidado. Desde o primeiro contato até o acompanhamento pós-consulta, a Novità está ao seu lado.
                </p>
              </div>
              
              <div className="mt-12 space-y-4">
                {[
                  "Equipe Multidisciplinar Especializada",
                  "Foco Total na Experiência do Paciente",
                  "Tecnologia de Ponta e Segurança de Dados",
                  "Ética e Transparência em Cada Processo"
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center space-x-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-primary" />
                    </div>
                    <span className="font-bold text-text-main">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Differentials Grid */}
      <section className="section-padding bg-bg-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl lg:text-5xl font-black text-text-main mb-6 font-display">
              Nossos Diferenciais
            </h2>
            <div className="w-20 h-1.5 bg-primary mx-auto rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {differentials.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                viewport={{ once: true }}
                className="group p-8 rounded-[2rem] bg-white border border-text-muted/10 hover:border-primary/30 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-14 h-14 bg-bg-light rounded-2xl flex items-center justify-center mb-6 group-hover:bg-primary group-hover:text-white transition-colors">
                  <item.icon className="h-7 w-7" />
                </div>
                <h3 className="text-xl font-bold text-text-main mb-3">{item.title}</h3>
                <p className="text-text-muted text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values - Modern Layout */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {[
              { title: "Missão", icon: Target, text: "Proporcionar atendimento de excelência, promovendo o bem-estar e a qualidade de vida das pessoas.", color: "bg-bg-light" },
              { title: "Visão", icon: Eye, text: "Ser referência nacional em assistência à saúde.", color: "bg-bg-light" },
              { title: "Valores", icon: Heart, text: "Humanização, ética, empatia, comprometimento, respeito ao ser humano.", color: "bg-bg-light" }
            ].map((item, idx) => (
              <div key={idx} className={`${item.color} p-10 rounded-[2.5rem] relative overflow-hidden group border border-text-muted/10`}>
                <div className="absolute -right-8 -bottom-8 opacity-10 group-hover:scale-110 transition-transform">
                  <item.icon className="w-40 h-40" />
                </div>
                <div className="relative z-10">
                  <div className="w-16 h-16 rounded-2xl bg-white shadow-sm flex items-center justify-center text-text-main mb-8">
                    <item.icon className="h-8 w-8" />
                  </div>
                  <h3 className="text-2xl font-black text-text-main mb-4 font-display">{item.title}</h3>
                  <p className="text-text-muted font-medium leading-relaxed">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us - Dark Mode Premium */}
      <section className="section-padding bg-text-main relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary rounded-full blur-[120px]"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary-dark rounded-full blur-[120px]"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-4xl lg:text-5xl font-black text-white mb-6 font-display">
              Por que escolher a Novità?
            </h2>
            <p className="text-text-muted max-w-2xl mx-auto text-lg">
              Excelência clínica combinada com tecnologia de ponta para sua total segurança.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyUs.map((item, index) => (
              <div key={index} className="flex items-start space-x-5 p-8 rounded-3xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all group">
                <div className="bg-primary/20 p-3 rounded-2xl group-hover:bg-primary transition-colors">
                  <CheckCircle2 className="h-6 w-6 text-primary group-hover:text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-text-muted leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PlansSection />

      {/* Final CTA */}
      <section className="section-padding bg-white relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-5xl lg:text-7xl font-black text-text-main mb-8 leading-tight font-display">
              Pronto para cuidar <br />da sua <span className="text-primary">saúde?</span>
            </h2>
            <p className="text-xl text-text-muted mb-12 max-w-2xl mx-auto font-medium">
              Junte-se a milhares de pacientes que já confiam na Novità para seu atendimento médico 24h.
            </p>
            <Link to="/signup" className="btn-primary px-12 py-6 text-xl">
              Começar Agora <ArrowRight className="ml-3 w-6 h-6" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
